/**
 * Composable to fetch data from ONLY from storyblok BETA space.
 *
 */
import {apiPlugin, storyblokInit, type StoryblokClient} from "@storyblok/js";
import type {ISbResult} from "storyblok-js-client";
import {type ReffedParameters, unrefParameters} from "../helpers/storyblok";
import {
	useAsyncDataWithPausing,
	type AsyncDataOptionsWithPausing,
} from "./use-async-data-with-pausing";

import {isRef} from "vue";
import {useRuntimeConfig} from "nuxt/app";

let betaApiSingleton: StoryblokClient | undefined;

function getBetaApi() {
	const {
		public: {storyblokBeta},
	} = useRuntimeConfig();

	if (betaApiSingleton) {
		return betaApiSingleton;
	}

	if (!storyblokBeta.accessToken) {
		throw new Error("Storyblok beta API access token not set");
	}

	const {storyblokApi} = storyblokInit({
		accessToken: storyblokBeta.accessToken,
		use: [apiPlugin],
		apiOptions: {
			cache: {type: "memory", clear: "manual"},
		},
	});

	if (!storyblokApi) {
		throw new Error("Storyblok beta API not initialized");
	}

	betaApiSingleton = storyblokApi;
	return storyblokApi;
}

export function useStoryblokBetaGet(
	key: string,
	path: string,
	parameters: ReffedParameters = {},
	asyncOptions: AsyncDataOptionsWithPausing<ISbResult> = {},
) {
	const watchValues = Object.values(parameters).filter((value) => isRef(value));

	return useAsyncDataWithPausing(
		key,
		() =>
			getBetaApi().get(`cdn/${path}`, {
				...unrefParameters(parameters),
				version:
					useRuntimeConfig().public.storyblokVersion === "published"
						? "published"
						: "draft",
			}),
		{
			watch: watchValues,
			...asyncOptions,
		},
	);
}
